import { w3cwebsocket } from 'websocket'
import Bus from '@/assets/js/bus.js'
import Vue from 'vue'
import i18n from '@/lang'
import { Message } from 'element-ui'
import { wsDispatcher } from '@/assets/js/wsDispatcher' 

// 全局状态管理
// 多个连接断开时只显示一条通知
// 所有连接恢复后才能将通知关闭
const globalWsState = {
  disconnectedCount: 0,    // 当前断开的连接数
  notification: null,       // 全局通知实例
  connections: new Set(),   // 所有活跃连接
}

/**
 * @param {*} options separate error callback
 * @example  {callback: {80400012: ()=>{}}}
 */
function enhancerMessage (options) {
  return function (event) {
    if (event.data == '2') return // heartbeat don't need deal
    const data = JSON.parse(event.data)
    const { errorCode } = data
    if (errorCode && options.callback) {
      options.callback[errorCode] && options.callback[errorCode](data)
      return
    }
    options.message(data)
    wsDispatcher.dispatch(data)
  }
}

/**
 * 更新全局连接状态
 * @param {WebsocketManage} parentObj 
 * @param {boolean} isConnected 
 */
function updateGlobalConnectionState (parentObj, isConnected) {
  const { id } = parentObj

  if (isConnected) {
    // 连接恢复
    if (globalWsState.connections.has(id)) {
      globalWsState.disconnectedCount--
      globalWsState.connections.delete(id)
    }
  } else {
    // 连接断开
    if (!globalWsState.connections.has(id)) {
      globalWsState.disconnectedCount++
      globalWsState.connections.add(id)
    }
  }

  // 处理通知显示
  if (globalWsState.disconnectedCount > 0) {
    if (!globalWsState.notification) {
      globalWsState.notification = Message({
        message: i18n.t('responeInfo.wsDisconnected'),
        type: 'warning',
        offset: 50,
        showClose: true,
        duration: 0,
      })
    }
  } else if (globalWsState.notification) {
    globalWsState.notification.close()
    globalWsState.notification = null
  }
}

/**
 *
 * @param { String } url connect ws url
 * @param { Object } options about ws options
 * @param { websocketManage } parentObj
 * @returns { w3cwebsocket }  ws connect
 */
function createWs (url, options, parentObj) {
  let reconnectTimeout = null
  let heartbeatTimer = null
  const NewW3cwebsocket = w3cwebsocket
  const client = new NewW3cwebsocket(url)
  client.onopen = (e) => {
    console.log('WebSocket connected')
    // 更新全局连接状态（已连接）
    updateGlobalConnectionState(parentObj, true)
    const that = client
    function sendNumber () {
      if (that.readyState === that.OPEN) {
        that.send('1')
        clearTimeout(heartbeatTimer)
        heartbeatTimer = setTimeout(sendNumber, 3000)
        parentObj.consecutiveErrorCount = 0; // 成功连接后重置连续错误次数
      } else {
        // websocket error status, need to disconnect and reconnect
        that.close()
      }
    }
    sendNumber()
    // websocket重连之后的操作逻辑
    if (parentObj) {
      Bus.$emit('websocketReconnection')
    }
  }
  client.onclose =
    options.close ||
    function (e) {
      parentObj.consecutiveErrorCount++;
      // 非用户主动关闭时显示断开提示
      if (!parentObj.isUserClose) {
        console.log('WebSocket connection failed')
        // 更新全局连接状态（已断开）
        updateGlobalConnectionState(parentObj, false)
      }
      // If it is not triggered manually, it needs to be reconnected
      if (parentObj.isUserClose === false && parentObj.consecutiveErrorCount < 20) {
        // parentObj.consecutiveErrorCount++
        clearTimeout(reconnectTimeout)
        reconnectTimeout = setTimeout(() => {
          parentObj.client = createWs(url, options, parentObj)
        }, 2300)
      } else if (!parentObj.isUserIgnored && parentObj.isUserClose === false) {
        Vue.prototype.$alert('The system has been disconnected due to a poor network, unable to connect to WebSocket server, please click “refresh”  to reload the page.', {
          confirmButtonText: 'Refresh',
          customClass: 'reloadAlert',
          confirmButtonClass: 'reloadBtn',
          showClose: true,
          showCancelButton: false,
          closeOnClickModal: false,
          closeOnPressEscape: false,
          modal: false
        }).then(() => {
          location.reload(); // 重新加载页面
        }, () => {
          parentObj.isUserIgnored = true;
        })
      }
    }
  client.onmessage = enhancerMessage(options)
  client.onerror = error => {
    if (!parentObj.isUserClose && parentObj.consecutiveErrorCount < 20) {
      clearTimeout(reconnectTimeout)
      reconnectTimeout = setTimeout(() => {
        parentObj.consecutiveErrorCount++; // 发生错误时增加连续错误次数
        parentObj.client = createWs(url, options, parentObj)
      }, 2300)
    }
  }

  return client
}
/**
 * @param { String } url create websocket url
 * @param { Object } options include (close,message,callback)  call callback when ws error
 * @example options { close?: ()=>{}, message: ()=>{}, callback => {}}
 */
class WebsocketManage {
  constructor(url, options) {
    this.$url = url
    this.$options = options
    this.isUserClose = false
    this.consecutiveErrorCount = 0 // 连续错误计数，用来确定是否需要重连
    this.isUserIgnored = false
    this.id = Symbol(url) // 为每个连接创建唯一标识
    if (!options.message) {
      return
    }
    this.client = createWs(this.$url, this.$options, this)
  }

  /**
   *  close websocket method
   */
  close () {
    this.isUserClose = true
    this.client.close()
    // 从全局状态中移除
    if (globalWsState.connections.has(this.id)) {
      globalWsState.disconnectedCount--
      globalWsState.connections.delete(this.id)
    }
    // 检查是否需要关闭通知
    if (globalWsState.disconnectedCount === 0 && globalWsState.notification) {
      globalWsState.notification.close()
      globalWsState.notification = null
    }
  }

  /**
   * @param {Object} params send back-end message
   * @example include {type,data}, According to the back-end API
   */
  send (params) {
    this.client.send(JSON.stringify(params))
  }
}

function websocket (url, options) {
  return new WebsocketManage(url, options)
}

export default websocket
